import React, { ReactElement, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Field, useField } from 'formik';

import Trans from 'components/Common/Translation/Trans';
import { formatPrice } from 'services/formatter';

import styles from './GenericShippingMethod.module.scss';

type GenericPaymentMethodProps = {
  value: string;
  methodIcon: ReactNode;
  methodLabel: ReactNode;
  price: number;
  freeFrom?: ReactElement | string | number;
  classes?: {
    methodTitle?: string;
    methodContent?: string;
  };
  estimateDeliveryDates?: string[];
};

export default function GenericShippingMethod({
  value,
  methodIcon,
  methodLabel,
  price,
  freeFrom,
  classes,
  children,
  estimateDeliveryDates: dates,
}: React.PropsWithChildren<GenericPaymentMethodProps>): JSX.Element {
  const { t } = useTranslation(['checkout']);
  const fieldName = 'shippingMethod';
  const [{ value: fieldValue }, , { setValue }] = useField(fieldName);
  const isChecked = fieldValue === value;
  const deliveryEstimationMessage = useMemo((): string => {
    if (!dates?.length) {
      return '';
    }

    const locale = process.env.REACT_APP_CURRENCY_LOCALES_CODE;
    const weekdayOptions = { weekday: 'long' } as Intl.DateTimeFormatOptions;
    const options = {
      year: 'numeric',
      day: '2-digit',
      month: '2-digit',
    } as Intl.DateTimeFormatOptions;

    const formatDate = (date: string) => {
      const dateObj = new Date(date);
      const dayLong = new Intl.DateTimeFormat(locale, weekdayOptions).format(
        dateObj
      );
      const dateFormat = dateObj.toLocaleDateString(locale, options).split('/');

      return t('SHIPPING_GENERIC_DELIVERY_ESTIMATE_DATE_FORMAT', {
        dayLong,
        dayNumber: dateFormat[0],
        monthNumber: dateFormat[1],
      });
    };

    const dateStringFormatted = dates.map(formatDate);

    return t('SHIPPING_GENERIC_DELIVERY_ESTIMATE_DATES', {
      count: dateStringFormatted.length,
      beginDate: dateStringFormatted[0],
      endDate: dateStringFormatted[1] || '',
    });
  }, [dates]);

  const freeFromMessage =
    freeFrom && typeof freeFrom === 'number' ? (
      <Trans
        i18nKey={'SHIPPING_GENERIC_FREE_FROM'}
        namespace={'checkout'}
        values={{ price: formatPrice(freeFrom, true) }}
      />
    ) : (
      freeFrom
    );

  function onClickContainer(): void {
    if (!isChecked) {
      setValue(value);
    }
  }

  return (
    <div
      onClick={onClickContainer}
      className={classNames(styles.methodRow, {
        [styles.checked]: isChecked,
      })}
    >
      <div className={classNames(styles.methodTitle, classes?.methodTitle)}>
        <div className={styles.inputContainer}>
          <div className={styles.inputWrapper}>
            <Field
              type="radio"
              name={fieldName}
              value={value}
              id={`method-${value}`}
              className={styles.selectShippingMethodRadio}
            />
          </div>
          <span className={styles.price}>
            {price > 0 ? `+${formatPrice(price)}` : t('SHIPPING_PRICE_FREE')}
          </span>
          {freeFromMessage && (
            <span className={styles.freeFrom}>{freeFromMessage}</span>
          )}
        </div>
        <div className={styles.labelContainer}>
          <div className={styles.icon}>{methodIcon}</div>
          <label className={styles.label} htmlFor={`method-${value}`}>
            {methodLabel}
          </label>
          {deliveryEstimationMessage && (
            <div className={styles.delay}>{deliveryEstimationMessage}</div>
          )}
          <div className={styles.priceContainer}>
            <div className={styles.price}>
              {price > 0 ? `+${formatPrice(price)}` : t('SHIPPING_PRICE_FREE')}
            </div>
            {freeFromMessage && (
              <div className={styles.freeFrom}>{freeFromMessage}</div>
            )}
          </div>
        </div>
      </div>
      <div className={classNames(styles.methodContent, classes?.methodContent)}>
        {children}
      </div>
    </div>
  );
}
